import SelectListItemModel from "../../../custom/selectlist-item-model";
import ko from "knockout";
import {customFetch as fetch} from "../../../fetch";
import {errorToast, successToast, warningToast} from "../../../toastr-helper";
import moment from "moment";

class Leave {
    id: string;
    startDate: string;
    endDate: string;
    hours: number;
    created: string;
    type: string;

    constructor(model: any) {
        this.id = model.id;
        this.startDate = model.startDate;
        this.endDate = model.endDate;
        this.hours = model.hours;
        this.created = model.created;
        this.type = model.type;
    }
}

class SickLeave {
    id: string;
    startDate: string;
    endDate: string;
    hours: number;
    sickDays: string;

    constructor(model: any) {
        this.id = model.id;
        this.startDate = model.startDate;
        this.endDate = model.endDate;
        this.hours = model.hours;
        this.sickDays = model.sickDays;
    }
}

class Declaration {
    parent: EmployeeFixedHours;
    id: string
    amount: number;
    description: string;
    itemCode: string;
    itemTypeId: string;
    itemTypeText: string;

    statusSuccess: KnockoutObservable<boolean>;
    error: KnockoutObservable<string>;

    constructor(model: any, parent: EmployeeFixedHours) {
        this.parent = parent;
        this.id = model.id;
        this.amount = model.amount;
        this.description = model.description;
        this.itemCode = model.itemCode;
        this.itemTypeId = model.itemTypeId;
        this.itemTypeText = this.parent.declarationItemTypeOptions().find(opt => opt.id() === this.itemTypeId).name();

        this.statusSuccess = ko.observable<KnockoutObservable<boolean>>(model.status ?? false);
        this.error = ko.observable<string>(model.error ?? '');
    }

    async removeDeclaration() {
        await this.parent.removeDeclaration(this);
    }

    retry = () => {
        // Implement retry logic here
    }
}

interface FixedHourPayoutDeclaration {
    id?: string;  // Make id optional
    amount: number;
    description: string;
    itemCode: string;
    itemTypeId: string;
    itemTypeText: string;
    status?: string;
    error?: string;
}


class EmployeeFixedHours {
    parent: EmployeesCluster;
    id: KnockoutObservable<string>;
    employeeId: string;
    name: string;
    contractHours: number;

    status: boolean;
    error: string;

    periodStart: string;
    periodEnd: string;
    periodType: number;
    monthNumber: KnockoutObservable<number>;
    weekNumber: KnockoutObservable<number>;

    workedHours: KnockoutObservable<number>;
    storedWorkedHours: KnockoutObservable<number>;
    leaveHours: KnockoutObservable<number>;
    storedLeaveHours: KnockoutObservable<number>;
    sickleaveHours: KnockoutObservable<number>;
    storedSickleaveHours: KnockoutObservable<number>;
    sickleaveWaitingDayHours: KnockoutObservable<number>;
    storedSickleaveWaitingDayHours: KnockoutObservable<number>;
    hoursSum: KnockoutComputed<number>;

    sickPercentages: KnockoutObservableArray<SelectListItemModel>;
    selectedSickPercentage: KnockoutObservable<string>;
    storedSelectedSickPercentage: KnockoutObservable<string>;
    declarations: KnockoutObservableArray<Declaration>;
    declarationOptions: KnockoutObservableArray<SelectListItemModel>;
    selectedDeclarationOption: KnockoutObservable<string>;
    declarationItemTypeOptions: KnockoutObservableArray<SelectListItemModel>;
    selectedDeclarationItemType: KnockoutObservable<string>;

    expanded: KnockoutObservable<boolean>;

    declarationValue: KnockoutObservable<number>;
    declarationType: KnockoutObservable<string>;

    oneTimeRemark: KnockoutObservable<string>;
    storedOneTimeRemark: KnockoutObservable<string>;
    recurringRemark: KnockoutObservable<string>;
    storedRecurringRemark: KnockoutObservable<string>;

    sentBy: KnockoutObservable<string>;

    workedHoursStatusSuccess: KnockoutObservable<boolean>;
    leaveHoursStatusSuccess: KnockoutObservable<boolean>;
    sickleaveHoursStatusSuccess: KnockoutObservable<boolean>;
    waitingDayHoursStatusSuccess: KnockoutObservable<boolean>;

    workedHoursError: KnockoutObservable<string>;
    leaveHoursError: KnockoutObservable<string>;
    sickleaveHoursError: KnockoutObservable<string>;
    waitingDayHoursError: KnockoutObservable<string>;

    leaves: Leave[];
    sickLeaves: SickLeave[];

    pendingChanges: KnockoutComputed<boolean>;
    sending: KnockoutObservable<boolean>;
    storing: KnockoutObservable<boolean>;
    storingDeclaration: KnockoutObservable<boolean>;
    canBeSent: KnockoutComputed<boolean>;
    ignoring: KnockoutObservable<boolean>;
    blocking: KnockoutObservable<boolean>;
    blocked: KnockoutObservable<boolean>;

    constructor(model: any, parent: any) {
        this.parent = parent;
        this.id = ko.observable<string>(model.id);
        this.employeeId = model.employeeId;
        this.name = model.name;
        this.contractHours = model.contractHours;

        this.status = model.status;
        this.error = model.error;

        this.periodStart = model.periodStart;
        this.periodEnd = model.periodEnd;
        this.periodType = model.periodType;
        this.monthNumber = ko.observable<number>(model.monthNumber);
        this.weekNumber = ko.observable<number>(model.weekNumber);

        this.workedHours = ko.observable<number>(model.workedHours);
        this.storedWorkedHours = ko.observable<number>(model.workedHours);
        this.leaveHours = ko.observable<number>(model.leaveHours);
        this.storedLeaveHours = ko.observable<number>(model.leaveHours);
        this.sickleaveHours = ko.observable<number>(model.sickleaveHours)
        this.storedSickleaveHours = ko.observable<number>(model.sickleaveHours);
        this.sickleaveWaitingDayHours = ko.observable<number>(model.sickleaveWaitingDayHours);
        this.storedSickleaveWaitingDayHours = ko.observable<number>(model.sickleaveWaitingDayHours);
        this.hoursSum = ko.computed<number>(() =>
            Number((
                Number(this.workedHours()) +
                Number(this.leaveHours()) +
                Number(this.sickleaveHours()) +
                Number(this.sickleaveWaitingDayHours())
            ).toFixed(2))
        );

        this.sickPercentages = ko.observableArray<SelectListItemModel>([
            new SelectListItemModel({id: 'F013-01', name: '100%'}),
            new SelectListItemModel({id: 'F014-01', name: '95%'}),
            new SelectListItemModel({id: 'F014-02', name: '75%'})
        ]);


        this.declarationOptions = ko.observableArray<SelectListItemModel>([new SelectListItemModel({
            id: 'invalid',
            name: 'Geen prijsafspraken gevonden voor deze periode in AFAS'
        })]);
        this.declarationItemTypeOptions = ko.observableArray<SelectListItemModel>([
            new SelectListItemModel({id: '1', name: 'Werksoort'}),
            new SelectListItemModel({id: '6', name: 'Kosten'})
        ]);
        this.selectedDeclarationItemType = ko.observable<string>(model.selectedDeclarationItemType ?? '1');


        this.selectedSickPercentage = ko.observable<string>(model.selectedSickPercentage ?? 'F014-01');
        this.storedSelectedSickPercentage = ko.observable<string>(model.selectedSickPercentage ?? 'F014-01')
        this.declarations = ko.observableArray<Declaration>(model.declarations !== undefined ? model.declarations.map((t: any) => new Declaration(t, this)) : []);
        this.selectedDeclarationOption = ko.observable<string>(model.selectedDeclarationOption ?? 'F01-0111');

        this.expanded = ko.observable<boolean>(false);

        this.declarationValue = ko.observable<number>(null);
        this.declarationType = ko.observable<string>('');

        this.pendingChanges = ko.observable<boolean>(model.pendingChanges ?? false);

        this.oneTimeRemark = ko.observable<string>(model.oneTimeRemark ?? '');
        this.storedOneTimeRemark = ko.observable<string>(model.oneTimeRemark ?? '');
        this.recurringRemark = ko.observable<string>(model.recurringRemark ?? '');
        this.storedRecurringRemark = ko.observable<string>(model.recurringRemark ?? '');

        this.sentBy = ko.observable<string>(model.sentBy ?? 'Onbekend');

        this.workedHoursStatusSuccess = ko.observable<KnockoutObservable<boolean>>(model.workedHoursStatus ?? false);
        this.leaveHoursStatusSuccess = ko.observable<KnockoutObservable<boolean>>(model.leaveHoursStatus ?? false);
        this.sickleaveHoursStatusSuccess = ko.observable<KnockoutObservable<boolean>>(model.sickleaveHoursStatus ?? false);
        this.waitingDayHoursStatusSuccess = ko.observable<KnockoutObservable<boolean>>(model.waitingDayHoursStatus ?? false);

        this.workedHoursError = ko.observable<string>(model.workedHoursError ?? '');
        this.leaveHoursError = ko.observable<string>(model.leaveHoursError ?? '');
        this.sickleaveHoursError = ko.observable<string>(model.sickleaveHoursError ?? '');
        this.waitingDayHoursError = ko.observable<string>(model.waitingDayHoursError ?? '');

        this.leaves = model.leaves !== null && model.leaves !== undefined ? model.leaves.map((l: any) => new Leave(l)) : [];
        this.sickLeaves = model.sickLeaves !== null && model.leaves !== undefined ? model.sickLeaves.map((sl: any) => new SickLeave(sl)) : [];

        this.pendingChanges = ko.computed<boolean>(() => {
            const currentWorkedHours = Number(this.workedHours());
            const currentLeaveHours = Number(this.leaveHours());
            const currentSickleaveHours = Number(this.sickleaveHours());
            const currentSickleaveWaitingDayHours = Number(this.sickleaveWaitingDayHours());
            const currentSickPercentage = this.selectedSickPercentage();
            const currentOneTimeRemark = this.oneTimeRemark();
            const currentRecurringRemark = this.recurringRemark();

            return currentWorkedHours !== Number(this.storedWorkedHours()) ||
                currentLeaveHours !== Number(this.storedLeaveHours()) ||
                currentSickleaveHours !== Number(this.storedSickleaveHours()) ||
                currentSickleaveWaitingDayHours !== Number(this.storedSickleaveWaitingDayHours()) ||
                currentSickPercentage !== this.storedSelectedSickPercentage() ||
                currentOneTimeRemark !== this.storedOneTimeRemark() ||
                currentRecurringRemark !== this.storedRecurringRemark();
        });

        this.sending = ko.observable<boolean>(false);
        this.storing = ko.observable<boolean>(false);
        this.storingDeclaration = ko.observable<boolean>(false);
        this.canBeSent = ko.computed<boolean>(() => {
            return this.id() !== '00000000-0000-0000-0000-000000000000' && !this.pendingChanges();
        });
        this.ignoring = ko.observable<boolean>(false);
        this.blocking = ko.observable<boolean>(false);
        this.blocked = ko.observable<boolean>(model.blocked);
    }

    async addDeclaration() {
        if (this.storingDeclaration())
            return;

        this.storingDeclaration(true);
        if (this.selectedDeclarationOption() === 'invalid') {
            errorToast('Selecteer een actieve prijsafspraak');
            this.storingDeclaration(false);
            return
        }

        const declarationData: FixedHourPayoutDeclaration = {
            amount: this.declarationValue(),
            description: this.declarationOptions().find(opt => opt.id() === this.selectedDeclarationOption()).name(),
            itemCode: this.selectedDeclarationOption(),
            itemTypeId: this.selectedDeclarationItemType()
        };

        if (this.id() == '00000000-0000-0000-0000-000000000000') {
            this.id(await this.store());
        }

        const response = await fetch(`/Admin/FixedHoursPayout/SaveDeclaration?fixedHoursPayoutId=${this.id()}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(declarationData)
        });

        if (response.ok) {
            const declaration = new Declaration({
                ...declarationData,
                id: response.data,
                status: 'success'
            }, this);
            this.declarations.push(declaration);
            this.declarationValue(null);
        }
        this.storingDeclaration(false);
    }

    async removeDeclaration(declaration: Declaration) {
        const response = await fetch(`/Admin/FixedHoursPayout/RemoveDeclaration?fixedHoursPayoutId=${this.id()}&declarationId=${declaration.id}`, {
            method: 'DELETE'
        });

        if (response.ok) {
            this.declarations.remove(declaration);
        } else {
            errorToast(`Verwijderen van declaratie mislukt reden: ${response.errors}`);
        }
    }

    async expand() {
        this.expanded(!this.expanded());
        if (this.expanded()) {
            const response = await fetch(`/Admin/FixedHoursPayout/GetDeclarations?id=${this.employeeId}&periodStart=${this.periodStart}&periodEnd=${this.periodEnd}`, {
                method: 'GET'
            });

            if (response.ok && response.data.length > 0) {
                this.declarationOptions(response.data.map((d: any) => new SelectListItemModel(d)));
            }
        }
    }

    async sendToAfas() {
        if (this.sending())
            return;

        this.sending(true);

        if (this.id() === '00000000-0000-0000-0000-000000000000' || this.pendingChanges()) {
            this.id(await this.store());
        }

        const response = await fetch(`/Admin/FixedHoursPayout/sendToAfas?ids=${this.id()}`, {
            method: 'POST'
        });

        if (response.ok) {
            successToast('Werkuren verzonden');

            this.sending(false);
            await this.parent.parent.parent.refreshSend();
            await this.parent.parent.parent.refreshDropouts();
            this.parent.removeEmployeeFixedHours(this);
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));            
        } else {
            errorToast('Er is iets misgegaan. Verzenden mislukt.')
        }
        
        this.sending(false);
    }

    async retry() {
        if (this.sending())
            return;

        this.sending(true);

        const response = await fetch(`/Admin/FixedHoursPayout/Retry?id=${this.id()}`, {
            method: 'POST'
        });

        if (response.ok) {
            successToast('Werkuren verzonden');
            this.sending(false);
            await this.parent.parent.parent.refreshSend();
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Er is iets misgegaan. Verzenden mislukt.')
        }
        
        if (!response.ok) {

            // TODO: this doesn't work yet
            // TODO: ok great, but what would be the purpose of this in the future? 
            
            const updatedEmployeeFixedHours = new EmployeeFixedHours(response.data, this.parent);

            this.id(updatedEmployeeFixedHours.id());
            this.workedHours(updatedEmployeeFixedHours.workedHours());
            this.leaveHours(updatedEmployeeFixedHours.leaveHours());
            this.sickleaveHours(updatedEmployeeFixedHours.sickleaveHours());
            this.sickleaveWaitingDayHours(updatedEmployeeFixedHours.sickleaveWaitingDayHours());
            this.selectedSickPercentage(updatedEmployeeFixedHours.selectedSickPercentage());
            this.oneTimeRemark(updatedEmployeeFixedHours.oneTimeRemark());
            this.recurringRemark(updatedEmployeeFixedHours.recurringRemark());
            this.declarations(updatedEmployeeFixedHours.declarations());

            this.storedWorkedHours = updatedEmployeeFixedHours.storedWorkedHours;
            this.storedLeaveHours = updatedEmployeeFixedHours.storedLeaveHours;
            this.storedSickleaveHours = updatedEmployeeFixedHours.storedSickleaveHours;
            this.storedSickleaveWaitingDayHours = updatedEmployeeFixedHours.storedSickleaveWaitingDayHours;
            this.storedSelectedSickPercentage = updatedEmployeeFixedHours.storedSelectedSickPercentage;
            this.storedOneTimeRemark = updatedEmployeeFixedHours.storedOneTimeRemark;
            this.storedRecurringRemark = updatedEmployeeFixedHours.storedRecurringRemark;

            this.workedHoursStatusSuccess(updatedEmployeeFixedHours.workedHoursStatusSuccess());
            this.leaveHoursStatusSuccess(updatedEmployeeFixedHours.leaveHoursStatusSuccess());
            this.sickleaveHoursStatusSuccess(updatedEmployeeFixedHours.sickleaveHoursStatusSuccess());
            this.waitingDayHoursStatusSuccess(updatedEmployeeFixedHours.waitingDayHoursStatusSuccess());

            this.workedHoursError(updatedEmployeeFixedHours.workedHoursError());
            this.leaveHoursError(updatedEmployeeFixedHours.leaveHoursError());
            this.sickleaveHoursError(updatedEmployeeFixedHours.sickleaveHoursError());
            this.waitingDayHoursError(updatedEmployeeFixedHours.waitingDayHoursError());
        }
        
        this.sending(false);
    }

    async store() {
        if (this.storing())
            return;

        this.storing(true);
        let fd = new FormData();

        fd.append('id', this.id());
        fd.append('employeeId', this.employeeId);
        fd.append('periodStart', this.periodStart);
        fd.append('periodEnd', this.periodEnd);
        fd.append('contractHours', this.contractHours.toString().replace('.', ','));
        fd.append('workedHours', this.workedHours().toString().replace('.', ','));
        fd.append('leaveHours', this.leaveHours().toString().replace('.', ','));
        fd.append('sickleaveHours', this.sickleaveHours().toString().replace('.', ','));
        fd.append('sickleaveWaitingDayHours', this.sickleaveWaitingDayHours().toString().replace('.', ','));
        fd.append('selectedSickPercentage', this.selectedSickPercentage());
        fd.append('oneTimeRemark', this.oneTimeRemark());
        fd.append('recurringRemark', this.recurringRemark());
        this.leaves.forEach((leave, index) => {
            fd.append(`Leaves[${index}].id`, leave.id);
            fd.append(`Leaves[${index}].startDate`, leave.startDate);
            fd.append(`Leaves[${index}].endDate`, leave.endDate);
            fd.append(`Leaves[${index}].hours`, leave.hours.toString());
            fd.append(`Leaves[${index}].created`, leave.created);
            fd.append(`Leaves[${index}].type`, leave.type);
        });
        this.sickLeaves.forEach((sickLeave, index) => {
            fd.append(`SickLeaves[${index}].id`, sickLeave.id);
            fd.append(`SickLeaves[${index}].startDate`, sickLeave.startDate);
            fd.append(`SickLeaves[${index}].endDate`, sickLeave.endDate);
            fd.append(`SickLeaves[${index}].hours`, sickLeave.hours.toString());
            fd.append(`SickLeaves[${index}].sickDays`, sickLeave.sickDays);
        });

        const response = await fetch(`/Admin/FixedHoursPayout/StoreFixedHours`, {
            method: 'POST',
            body: fd
        });

        if (response.ok) {
            successToast('Werkuren opgeslagen');
            this.id(response.data);
            this.storedLeaveHours(Number(this.leaveHours()));
            this.storedSickleaveHours(Number(this.sickleaveHours()));
            this.storedWorkedHours(Number(this.workedHours()));
            this.storedRecurringRemark(this.recurringRemark());
            this.storedOneTimeRemark(this.oneTimeRemark());
            this.storedSickleaveWaitingDayHours(Number(this.sickleaveWaitingDayHours()));
            this.storedSelectedSickPercentage(this.selectedSickPercentage());

            this.storing(false);
            return response.data;
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Er is iets misgegaan. Werkuren kon niet opgeslagen worden.');
        }
        
        this.storing(false);
    }

    async ignoreForPeriod() {
        if (this.ignoring()) {
            return
        }
        this.ignoring(true);

        const response = await fetch(`/Admin/FixedHoursPayout/IgnoreForPeriod?fixedPayoutId=${this.id()}&employeeId=${this.employeeId}&periodStart=${this.periodStart}&periodEnd=${this.periodEnd}`, {
            method: 'DELETE'
        });

        if (response.ok) {
            successToast('Medewerker genegeerd voor deze periode');
            this.parent.removeEmployeeFixedHours(this);
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Er is iets misgegaan. Medewerker kon niet genegeerd worden.');
        }

        this.ignoring(false);
    }

    async blockEmployee() {
        if (this.blocking()) {
            return
        }
        this.blocking(true);

        const response = await fetch(`/Admin/FixedHoursPayout/BlockEmployee?employeeId=${this.employeeId}`, {
            method: 'PUT'
        });

        if (response.ok) {
            successToast('Medewerker geblokkeerd');
            this.blocked(true);
            this.parent.parent.moveEmployeeToCluster(this, 'Geblokkeerd');
            this.parent.removeEmployeeFixedHours(this);
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Er is iets misgegaan. Medewerker kon niet geblokkeerd worden.');
        }

        this.blocking(false);
    }

    async unBlockEmployee() {
        if (this.blocking()) {
            return
        }
        this.blocking(true);

        const response = await fetch(`/Admin/FixedHoursPayout/UnBlockEmployee?employeeId=${this.employeeId}`, {
            method: 'PUT'
        });

        if (response.ok) {
            successToast('Medewerker geblokkeerd');
            this.blocked(false);
            if (this.workedHours() == this.contractHours) {
                this.parent.parent.moveEmployeeToCluster(this, 'Standaard');
                this.parent.removeEmployeeFixedHours(this);
            }
            else {
                this.parent.parent.moveEmployeeToCluster(this, 'Afwijkend');
                this.parent.removeEmployeeFixedHours(this);
            }
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Er is iets misgegaan. Medewerker kon niet gedeblokkeerd worden.');
        }

        this.blocking(false);
    }
}

class EmployeesCluster {
    parent: CompanyFixedHours;
    employeesFixedHours: KnockoutObservableArray<EmployeeFixedHours>;
    name: string;
    contractHours: number;
    workedHours: KnockoutObservable<number>;
    leaveHours: KnockoutObservable<number>;
    sickleaveHours: KnockoutObservable<number>;
    sickleaveWaitingDayHours: KnockoutObservable<number>;

    expanded: KnockoutObservable<boolean>;

    pendingChanges: KnockoutComputed<boolean>;
    sending: KnockoutComputed<boolean>;
    canBeSent: KnockoutComputed<boolean>;

    constructor(model: any, parent: any) {
        this.parent = parent;
        this.employeesFixedHours = ko.observableArray<EmployeeFixedHours>(model.employees.map((e: any) => new EmployeeFixedHours(e, this)));
        this.name = model.name;
        this.contractHours = model.contractHours;
        this.workedHours = ko.observable<number>(model.workedHours);
        this.leaveHours = ko.observable<number>(model.leaveHours);
        this.sickleaveHours = ko.observable<number>(model.sickleaveHours)
        this.sickleaveWaitingDayHours = ko.observable<number>(model.sickleaveWaitingDayHours);

        this.expanded = ko.observable<boolean>(false);

        this.pendingChanges = ko.computed(() => {
            return this.employeesFixedHours().every(e => e.pendingChanges());
        });
        this.sending = ko.computed(() => {
            return this.employeesFixedHours().every(e => e.sending());
        });
        this.canBeSent = ko.computed(() => {
            return this.employeesFixedHours().every(e => e.canBeSent());
        });
    }

    expand = () => {
        return this.expanded(!this.expanded())
    }

    async sendToAfas() {
        if (this.sending())
            return;

        if (this.pendingChanges() || this.employeesFixedHours().some(e => !e.canBeSent())) {
            warningToast('Er zijn niet opgeslagen wijzigingen in deze werkuren. Sla eerst alles op.');
        }

        let ids = this.employeesFixedHours().map(e => e.id());
        this.employeesFixedHours().forEach(e => e.sending(true));

        const response = await fetch(`/Admin/FixedHoursPayout/sendToAfas?${ids.map(id => `ids=${id}`).join('&')}`, {
            method: 'POST'
        });

        if (response.ok) {
            successToast('Werkuren verzonden');
            await this.parent.parent.refreshSend();
            await this.parent.parent.refreshDropouts();
            this.parent.removeCluster(this);
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast(`Verzenden van werkuren mislukt reden: ${response.errors}`);
        }
        
        this.employeesFixedHours().forEach(e => e.sending(false));
    }

    removeEmployeeFixedHours(hoursToRemove: EmployeeFixedHours) {
        this.employeesFixedHours.remove(hoursToRemove);
        if (this.employeesFixedHours().length === 0) {
            this.parent.removeCluster(this);
        }
    }
}

export class CompanyFixedHours {
    parent: any;
    companyId: string;
    name: string;
    contractHours: number;
    workedHours: KnockoutObservable<number>;
    leaveHours: KnockoutObservable<number>;
    sickleaveHours: KnockoutObservable<number>;
    sickleaveWaitingDayHours: KnockoutObservable<number>;

    employeeClusters: KnockoutObservableArray<EmployeesCluster>;

    expanded: KnockoutObservable<boolean>;

    pendingChanges: KnockoutComputed<boolean>;
    sending: KnockoutComputed<boolean>;
    canBeSent: KnockoutComputed<boolean>;

    constructor(model: any, parent: any) {
        this.parent = parent;
        this.companyId = model.companyId;
        this.name = model.name;
        this.contractHours = model.contractHours;
        this.workedHours = ko.observable<number>(model.workedHours);
        this.leaveHours = ko.observable<number>(model.leaveHours);
        this.sickleaveHours = ko.observable<number>(model.sickleaveHours);
        this.sickleaveWaitingDayHours = ko.observable<number>(model.sickleaveWaitingDayHours);

        this.employeeClusters = ko.observableArray<EmployeesCluster>(model.employeeClusters.map((ec: any) => new EmployeesCluster(ec, this)));

        this.expanded = ko.observable<boolean>(false);

        this.pendingChanges = ko.computed(() => {
            return this.employeeClusters().every(e => e.pendingChanges());
        });

        this.sending = ko.computed(() => {
            return this.employeeClusters().every(e => e.sending());
        });

        this.canBeSent = ko.computed(() => {
            return this.employeeClusters().every(e => e.canBeSent());
        });
    }

    processSelected() {

    }

    async sendToAfas() {
        if (this.sending())
            return;

        if (this.pendingChanges() && this.employeeClusters().some(ec => ec.employeesFixedHours().some(e => !e.canBeSent()))) {
            warningToast('Er zijn niet opgeslagen wijzigingen in deze werkuren. Sla eerst alles op.');
        }

        let ids = this.employeeClusters().flatMap(ec => ec.employeesFixedHours().map(e => e.id()));

        this.employeeClusters().forEach(ec =>
            ec.employeesFixedHours().forEach(e => e.sending(true))
        );

        const response = await fetch(`/Admin/FixedHoursPayout/sendToAfas?${ids.map(id => `ids=${id}`).join('&')}`, {
            method: 'POST'
        });

        if (response.ok) {
            successToast('Werkuren verzonden');
            await this.parent.refreshSend();
            await this.parent.refreshDropouts();
            this.parent.removeCompany(this);
        } else if (response.errors && response.errors.length > 0) {
            errorToast(response.errors.map((c: any) => c).join(','));
        } else {
            errorToast('Verzenden mislukt');
        }
        
        this.employeeClusters().forEach(ec =>
            ec.employeesFixedHours().forEach(e => e.sending(false))
        );
    }

    expand() {
        this.expanded(!this.expanded())
    }

    removeCluster(employeeCluster: EmployeesCluster) {
        this.employeeClusters.remove(employeeCluster);
        if (this.employeeClusters().length === 0) {
            this.parent.removeCompany(this);
        }
    }

    public moveEmployeeToCluster(employeeFixedHours: EmployeeFixedHours, clusterName: string) {
        let clusterToMoveTo = this.employeeClusters().find(ec => ec.name === clusterName);
        
        if (!clusterToMoveTo) {
            clusterToMoveTo = new EmployeesCluster({
                name: clusterName,
                contractHours: employeeFixedHours.contractHours,
                workedHours: employeeFixedHours.workedHours(),
                leaveHours: employeeFixedHours.leaveHours(),
                sickleaveHours: employeeFixedHours.sickleaveHours(),
                sickleaveWaitingDayHours: employeeFixedHours.sickleaveWaitingDayHours(),
                employees: []
            }, this);
            
            clusterToMoveTo.employeesFixedHours.push(employeeFixedHours);
            clusterToMoveTo.expanded(true);
            this.employeeClusters().push(clusterToMoveTo);
        } else {
            clusterToMoveTo.contractHours = clusterToMoveTo.contractHours + employeeFixedHours.contractHours;
            clusterToMoveTo.workedHours(clusterToMoveTo.workedHours() + employeeFixedHours.workedHours());
            clusterToMoveTo.leaveHours(clusterToMoveTo.leaveHours() + employeeFixedHours.leaveHours());
            clusterToMoveTo.sickleaveHours(clusterToMoveTo.sickleaveHours() + employeeFixedHours.sickleaveHours());
            clusterToMoveTo.sickleaveWaitingDayHours(clusterToMoveTo.sickleaveWaitingDayHours() + employeeFixedHours.sickleaveWaitingDayHours());
            clusterToMoveTo.expanded(true);
            
            clusterToMoveTo.employeesFixedHours.push(employeeFixedHours);
        }
        this.expanded(true);
    }
}
